'use strict';
const observable = require('@mindmup/mapjs-core/src/util/observable'),
	SAVE_INTERVAL = 1000,
	POLL_INTERVAL = 3000,
	RETRY_ENVELOPE_UPLOAD_MILLIS = 600000,
	THUMBNAIL_INTERVAL = 60000,
	GoldFileEventPoller = require('./gold-file-event-poller'),
	GoldFileEventWebSocketPoller = require('../models/gold-file-event-websocket-poller'),
	GoldFileHistoryModel = require('./gold-file-history-model'),
	GoldFileHistoryEvent = require('@mindmup/mindmup-common/src/gold-file-history-event'),
	unixTimestamp = require('@mindmup/js-utils/src/unix-timestamp'),
	raceAgainstTimeout = require('./race-against-timeout'),
	loadUnaggregatedEnvelopesImpl = require('./load-unaggregated-envelopes'),
	shouldRequestWatermarkImpl = require('./should-request-watermark');

module.exports = function GoldFileChangesModel({collaborationModel, s3FileModel, pageState, navigatorShim, networkStatusDispatcher, resourceManager, goldFileChangesCache, envelopeBuilder, goldFileHistoryEnvelopeSorter}, optional) {
	if (!collaborationModel || !s3FileModel || !pageState || !navigatorShim || !networkStatusDispatcher || !goldFileChangesCache || !envelopeBuilder || !goldFileHistoryEnvelopeSorter) {
		throw new Error('invalid-args');
	}

	let failCount = 0, started = false, uploadInProgress = false, lastThumbnailCounter = 0, currentSaveConfiguration, saveIntervalId, lastUploadTs = 0, pollingId = false, wasTrashed = false, historyExistedAtTimeOfLoading;

	const obs = observable(this),
		thumbnailBuilder = optional?.thumbnailBuilder,
		eventBuffer = [],
		goldFileHistoryModel = (optional && optional.goldFileHistoryModel) || new GoldFileHistoryModel(pageState, s3FileModel),
		goldFileHistoryEvent = (optional && optional.goldFileHistoryEvent) || new GoldFileHistoryEvent(collaborationModel.getLocalSessionId),
		loadUnaggregatedEnvelopes = (optional && optional.loadUnaggregatedEnvelopes) || loadUnaggregatedEnvelopesImpl,
		shouldRequestWatermark = (optional && optional.shouldRequestWatermark) || shouldRequestWatermarkImpl,
		goldSessionModel = (optional && optional.goldSessionModel),
		ensureSessionId = async () => {
			if (!collaborationModel.getLocalSessionId()) {
				const sessionId = await s3FileModel.startSession(pageState.getPathInfo());
				collaborationModel.setLocalSessionId(sessionId);
			}
			if (goldSessionModel) {
				await goldSessionModel.ensureConnection({fileId: pageState.getFileId(), sessionId: collaborationModel.getLocalSessionId(), clientSecret: s3FileModel.getClientSecret()});
			}
		},
		onUploadInProgress = function (isInProgress) {
			uploadInProgress = isInProgress;
			networkStatusDispatcher.notifySavingChanged(!!eventBuffer.length);
		},
		getSaveConfiguration = () => {
			if (currentSaveConfiguration && currentSaveConfiguration.expiry > (unixTimestamp() + 30)) {
				return Promise.resolve(currentSaveConfiguration);
			}

			const pathInfo = Object.assign({}, pageState.getPathInfo()),
				sessionId = collaborationModel.getLocalSessionId();

			return s3FileModel.goldFileChangesSaveConfiguration(pathInfo, sessionId)
			.then((saveConfiguration) => {
				currentSaveConfiguration = saveConfiguration;
				return currentSaveConfiguration;
			});
		},
		getMaxChangeSize = async () => {
			const pathInfo = Object.assign({}, pageState.getPathInfo()),
				config = !(pathInfo?.free || pathInfo?.new) && await getSaveConfiguration();
			return config?.maxSize;
		},
		uploadEvents = async events => {
			const results = await Promise.all([getSaveConfiguration(), envelopeBuilder.nextEnvelope(events)]),
				saveConfiguration = results[0],
				envelope = results[1],
				instanceSaveConfiguration = Object.assign({}, saveConfiguration, {key: saveConfiguration.key + envelope.header.sequence + saveConfiguration.fileExtension});
			return s3FileModel.saveGoldFile(instanceSaveConfiguration, envelope)
			.then(() => goldFileChangesCache.sent(envelope));
		},
		reuploadEnvelopes = async envelopes => {
			if (optional && optional.disableReupload) {
				return;
			}
			const saveConfiguration = await getSaveConfiguration();
			return Promise.all(envelopes.map(async envelope => {
				const instanceSaveConfiguration = Object.assign({}, saveConfiguration, {key: saveConfiguration.key + envelope.header.sequence + saveConfiguration.fileExtension});
				return s3FileModel.saveGoldFile(instanceSaveConfiguration, envelope);
			}));
		},
		uploadThumbnail = () => {
			if (!thumbnailBuilder) {
				return;
			}
			const oldThumbnailCounter = lastThumbnailCounter,
				counter = envelopeBuilder.getCounter();
			if (!lastThumbnailCounter || lastThumbnailCounter < counter) {
				lastThumbnailCounter = counter;
				return thumbnailBuilder.build()
				.then((dataUri) => s3FileModel.uploadAsset(pageState.getFileId(), dataUri, {isThumbnail: true}))
				.catch(() => lastThumbnailCounter = oldThumbnailCounter);
			}
			return Promise.resolve();
		},
		loadChangesHistory = (withRange) => {
			const range = withRange && {startByte: goldFileHistoryModel.getLastHistoryByte()};
			return goldFileHistoryModel.loadChangesHistory(range)
			.then(historyEnvelopes => {
				goldFileChangesCache.received(historyEnvelopes);
				return historyEnvelopes;
			})
			.then(historyEnvelopes => {
				if (!goldFileHistoryEnvelopeSorter.requiresSorting(historyEnvelopes)) {
					return historyEnvelopes;
				} else {
					return goldFileHistoryEnvelopeSorter.sortedEnvelopes(historyEnvelopes);
				}
			})
			.catch((reason) => {
				if (reason === 'not-found') {
					if (historyExistedAtTimeOfLoading && !range) {
						throw reason;
					} else if (!wasTrashed && range && range.startByte > 0) {
						wasTrashed = true;
						obs.dispatchEvent('trashed');
						return [];
					} else {
						return [];
					}
				}
				throw reason;
			});
		},
		processHistoryEnvelopes = (historyEnvelopes) => {
			if (!historyEnvelopes || !historyEnvelopes.length) {
				return;
			}
			obs.dispatchEvent('hasHistory');
			let newName = false, trashed = false, hadTrashedEvent = false;
			historyEnvelopes.forEach((envelope) => {
				if (goldFileChangesCache.exists(envelope)) {
					return;
				}
				goldFileChangesCache.store(envelope);
				const sessionId = envelope && envelope.header && envelope.header.sessionId,
					events = envelopeBuilder.eventsFromEnvelope(envelope);
				events.forEach((evt) => {
					if (goldFileHistoryEvent.isNonControlEvent(evt)) {
						collaborationModel.triggerRemoteContentEvent(evt, sessionId);
					} else if (goldFileHistoryEvent.isRenameEvent(evt)) {
						newName = evt.args && evt.args[0];
					}  else if (goldFileHistoryEvent.isTrashedEvent(evt)) {
						hadTrashedEvent = true;
						trashed = evt.args && evt.args[0];
					}
				});
			});
			if (newName) {
				obs.dispatchEvent('title', newName);
				pageState.renamed(newName);
			}
			if (hadTrashedEvent) {
				if (!wasTrashed && trashed) {
					wasTrashed = true;
					obs.dispatchEvent('trashed');
				} else if (wasTrashed && !trashed) {
					wasTrashed = false;
					obs.dispatchEvent('untrashed');
				}
			}
		},
		calcEventPoller = () => {
			if (optional && optional.goldFileEventPoller) {
				return optional.goldFileEventPoller;
			}
			const goldFileEventDefaultPoller = new GoldFileEventPoller(loadChangesHistory, goldFileChangesCache, collaborationModel, goldFileHistoryEvent, resourceManager, processHistoryEnvelopes);

			if (optional && optional.webSocketShim) {
				return new GoldFileEventWebSocketPoller({goldFileEventDefaultPoller, webSocketShim: optional.webSocketShim, goldFileHistoryModel});
			}
			return goldFileEventDefaultPoller;

		},
		goldFileEventPoller = calcEventPoller(),
		saveEvents = () => {
			const lastEvent = eventBuffer.length,
				eventsToUpload = eventBuffer.slice(0, lastEvent);
			if (uploadInProgress || !eventsToUpload.length) {
				return Promise.resolve();
			}
			onUploadInProgress(true);
			return raceAgainstTimeout(navigatorShim, uploadEvents(eventsToUpload), 20000)
			.then(() => {
				failCount = 0;
				eventBuffer.splice(0, lastEvent);
				lastUploadTs = Date.now();
				onUploadInProgress(false);
				obs.dispatchEvent('event-upload-success');
			})
			.catch((err) => {
				failCount++;
				currentSaveConfiguration = false;
				onUploadInProgress(false);
				if (failCount >= 2 && goldFileEventPoller.getShowSaveOnError()) {
					collaborationModel.error('networkError');
					goldFileEventPoller.setShowSaveOnError(false);
				}

				obs.dispatchEvent('event-upload-failed', err);
			});
		},
		notifySaveDelay = () => {
			const millisSinceLastUpload = (eventBuffer.length && (Date.now() - lastUploadTs)) || 0,
				millisMaxPendingReceipt = goldFileChangesCache.millisMaxPendingReceipt();
			networkStatusDispatcher.notifySaveDelay(Math.max(millisSinceLastUpload, millisMaxPendingReceipt));
		},
		retrySentChanges = () => {
			const pendingEnvelopes = goldFileChangesCache.envelopesPendingReciept(RETRY_ENVELOPE_UPLOAD_MILLIS);
			if (pendingEnvelopes.length) {
				return reuploadEnvelopes(pendingEnvelopes);
			}
		},
		beforeUnloadListener = (e) => {
			if (eventBuffer.length) {
				const msg = 'This document is still saving to changes. If you leave now, you might lose data. If you are experiencing connectivity problems, cancel and save the data locally using File->Download As->MindMup';
				e.returnValue = msg;
				e.preventDefault();
				return msg;
			}
		},
		ensureInitialised = () => {
			if (saveIntervalId) {
				return;
			}
			lastUploadTs = Date.now();
			saveIntervalId = navigatorShim.setInterval(saveEvents, SAVE_INTERVAL);
			navigatorShim.setInterval(notifySaveDelay, SAVE_INTERVAL);
			navigatorShim.setInterval(uploadThumbnail, THUMBNAIL_INTERVAL);
			navigatorShim.setInterval(retrySentChanges, RETRY_ENVELOPE_UPLOAD_MILLIS);
			uploadThumbnail();
		},
		bufferEvent = (evt) => {
			networkStatusDispatcher.notifySavingChanged(true);
			ensureInitialised();
			goldFileEventPoller.setShowSaveOnError(true);
			eventBuffer.push(evt);
			if (eventBuffer.length > 100) {
				obs.dispatchEvent('unsaved-event-queue-length', eventBuffer.length);
			}
		},
		setHistoryPath = (changesHistoryPathURL, initialHistoryExists) => {
			historyExistedAtTimeOfLoading = initialHistoryExists;
			goldFileHistoryModel.setHistoryPath(changesHistoryPathURL);
		},
		start = () => {
			if (started) {
				return;
			}
			started = true;
			networkStatusDispatcher.notifySavingChanged(false);
			return ensureSessionId()
			.then(() => {
				navigatorShim.addWindowEventListener('beforeunload', beforeUnloadListener);
				pollingId = navigatorShim.setInterval(goldFileEventPoller.pollForEvents, POLL_INTERVAL);
			})
			.then(() => obs.dispatchEvent('started'))
			.catch(e => {
				started = false;
				throw e;
			});
		},
		halt = () => {
			navigatorShim.removeWindowEventListener('beforeunload', beforeUnloadListener);
			if (pollingId) {
				navigatorShim.clearInterval(pollingId);
			}
			started = false;
			//not sure what should happen here
		},
		localControlEvent = (type, args) => {
			if (!started) {
				return;
			}
			const evt = goldFileHistoryEvent.buildLocalControlEvent(type, args);
			bufferEvent(evt);
		},
		loadEvents = (fromByte) => {
			let allEvents = [];
			const appendUniqueEventsFromEnvelopes = (envelopes) => {
				envelopes.forEach((envelope) => {
					if (goldFileChangesCache.exists(envelope)) {
						return;
					}
					goldFileChangesCache.store(envelope);
					allEvents = allEvents.concat(envelopeBuilder.eventsFromEnvelope(envelope));
				});
			};
			if (fromByte) {
				goldFileHistoryModel.setLastHistoryByte(fromByte);
			}
			return loadUnaggregatedEnvelopes(pageState, s3FileModel)
			.then((unaggregatedEnvelopes) => {
				return loadChangesHistory(!!fromByte)
				.then(hist => {
					if (shouldRequestWatermark(hist)) {
						s3FileModel.requestWatermark(pageState.getPathInfo());
					}
					return appendUniqueEventsFromEnvelopes(hist);
				})
				.then(() => appendUniqueEventsFromEnvelopes(unaggregatedEnvelopes));
			})
			.then(() => {
				const trashEvents = allEvents.filter(goldFileHistoryEvent.isTrashedEvent),
					lastTrashEvent = trashEvents.pop();
				wasTrashed = lastTrashEvent && lastTrashEvent.args && lastTrashEvent.args[0];
			})
			.then(() => allEvents.filter(goldFileHistoryEvent.isNonControlEvent))
			.then(results => {
				if (results && results.length) {
					obs.dispatchEvent('hasHistory');
				}
				return results;
			});
		},
		isTrashed = () => wasTrashed,
		deleteFile = (pathInfo) => {
			const evt = goldFileHistoryEvent.buildLocalControlEvent('trashed', [true]);
			return envelopeBuilder.nextEnvelope([evt])
			.then(envelope => s3FileModel.deleteFile(pathInfo, envelope))
			.then(() => wasTrashed = true);
		},
		undeleteFile = (pathInfo) => {
			const evt = goldFileHistoryEvent.buildLocalControlEvent('trashed', [false]);
			return envelopeBuilder.nextEnvelope([evt])
			.then(envelope => s3FileModel.undeleteFile(pathInfo, envelope))
			.then(() => wasTrashed = false);
		};
	Object.assign(this, {setHistoryPath, start, halt, localContentEvent: bufferEvent, localControlEvent, loadEvents, isTrashed, deleteFile, undeleteFile, getSaveConfiguration, getMaxChangeSize});
};
